/**
 * requests-new.ts
 * 
 * Servizio per la gestione delle richieste tramite API.
 * Allineato con le API del backend UfficioStile.
 */

import { apiClient, buildQueryParams } from './base';
import { toRequestTypeNum, toRequestStatusNum } from '../../utils/enum-normalizers';
import {
  Request,
  RequestCreateRequest,
  RequestUpdateRequest,
  RequestStatus,
  RequestType,
  ApiResponse,
  PagedResponse,
  SearchParams,
  ProjectTask,
  ProjectTaskCreateRequest,
  ProgressiveNumberSuggestion,
  ProgressiveNumberValidation,
  RequestAggregates,
  RequestAggregatesQuery,
  RequestAnalyticsRow,
  AnalyticsRequestsQuery
} from '../../types/api';export class RequestService {
  private baseUrl = '/api/Request';

  // ================== OPERAZIONI CRUD BASE ==================
  async getAllRequests(params?: SearchParams): Promise<PagedResponse<Request>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const response = await apiClient.get<PagedResponse<Request>>(url);
    return response.data;
  }

  async getRequestSummaries(params?: SearchParams): Promise<PagedResponse<Request>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString ? `${this.baseUrl}/summaries?${queryString}` : `${this.baseUrl}/summaries`;
    const response = await apiClient.get<PagedResponse<Request>>(url);
    return response.data;
  }

  async getRequestById(id: string): Promise<Request> {
    const response = await apiClient.get<Request>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async createRequest(data: RequestCreateRequest): Promise<Request> {
    const response = await apiClient.post<Request>(this.baseUrl, data);
    return response.data;
  }

  async updateRequest(id: string, data: RequestUpdateRequest): Promise<Request> {
    const response = await apiClient.put<Request>(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  async deleteRequest(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  // ================== OPERAZIONI STATO ==================
  async updateRequestStatus(id: string, newStatus: RequestStatus, notes?: string, completedAt?: string): Promise<void> {
    const data = { newStatus, notes, completedAt };
    await apiClient.post(`${this.baseUrl}/${id}/status`, data);
  }

  async getRequestsByStatus(status: RequestStatus): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/by-status/${status}`);
    return response.data;
  }

  async getOverdueRequests(): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/overdue`);
    return response.data;
  }

  async getRequestsDueSoon(): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/due-soon`);
    return response.data;
  }

  // ================== OPERAZIONI CLIENTI ==================
  async getRequestsByClient(clientId: string): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/by-client/${clientId}`);
    return response.data;
  }

  async getRequestsByClientName(clientName: string): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/by-client-name/${encodeURIComponent(clientName)}`);
    return response.data;
  }

  // ================== OPERAZIONI RICERCA E FILTRI ==================
  async searchRequests(params: {
    searchTerm?: string;
    status?: RequestStatus;
    type?: RequestType;
    clientId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResponse<Request>> {
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<PagedResponse<Request>>(`${this.baseUrl}/search?${queryString}`);
    return response.data;
  }

  async getRequestsByDateRange(startDate: string, endDate: string): Promise<Request[]> {
    const params = { startDate, endDate };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/by-date-range?${queryString}`);
    return response.data;
  }

  async getRequestsByType(
    type: RequestType,
    params?: {
      pageNumber?: number;
      pageSize?: number;
      searchTerm?: string;
      progressiveYear?: number;
      status?: RequestStatus;
      startDate?: string;
      endDate?: string;
      /** Filtro su più stati insieme (es. pagina Pre-Campionatura), in alternativa a `status` */
      statuses?: RequestStatus[];
    }
  ): Promise<PagedResponse<Request>> {
    const queryString = params ? buildQueryParams(params) : '';
    const url = queryString
      ? `${this.baseUrl}/by-type/${type}?${queryString}`
      : `${this.baseUrl}/by-type/${type}`;
    const response = await apiClient.get<PagedResponse<Request>>(url);
    return response.data;
  }

  async getAggregates(query?: RequestAggregatesQuery): Promise<RequestAggregates> {
    const queryString = query ? buildQueryParams(query) : '';
    const url = queryString
      ? `${this.baseUrl}/aggregates?${queryString}`
      : `${this.baseUrl}/aggregates`;
    const response = await apiClient.get<RequestAggregates>(url);
    return normalizeAggregates(response.data);
  }

  /**
   * Elenco richieste per grafici analytics (tempi di completamento) e drill-down.
   * Il BE serializza type/status come stringhe: normalizziamo a numeri.
   */
  async getAnalyticsRequests(query?: AnalyticsRequestsQuery): Promise<RequestAnalyticsRow[]> {
    const queryString = query ? buildQueryParams(query as Record<string, any>) : '';
    const url = queryString
      ? `${this.baseUrl}/analytics/requests?${queryString}`
      : `${this.baseUrl}/analytics/requests`;
    const response = await apiClient.get<RequestAnalyticsRow[]>(url);
    return (response.data ?? []).map(normalizeAnalyticsRow);
  }

  /**
   * Esporta in Excel (.xlsx) tutte le analytics delle richieste con grafici nativi
   * più il foglio di dettaglio. Ritorna il Blob del file; il filtro (anno/tipo/date)
   * è lo stesso usato dalle dashboard analytics.
   */
  async exportAnalytics(query?: RequestAggregatesQuery): Promise<Blob> {
    const queryString = query ? buildQueryParams(query) : '';
    const url = queryString
      ? `${this.baseUrl}/analytics/export?${queryString}`
      : `${this.baseUrl}/analytics/export`;
    return apiClient.download(url);
  }

  async getMyRequests(): Promise<Request[]> {
    const response = await apiClient.get<Request[]>(`${this.baseUrl}/my-requests`);
    return response.data;
  }

  // ================== OPERAZIONI TASK ==================
  async getRequestTasks(requestId: string): Promise<ProjectTask[]> {
    const response = await apiClient.get<ProjectTask[]>(`${this.baseUrl}/${requestId}/tasks`);
    return response.data;
  }

  async createRequestTask(requestId: string, taskData: ProjectTaskCreateRequest): Promise<ProjectTask> {
    const response = await apiClient.post<ProjectTask>(`${this.baseUrl}/${requestId}/tasks`, taskData);
    return response.data;
  }

  // ================== OPERAZIONI UTILITÀ ==================
  async duplicateRequest(id: string): Promise<Request> {
    const response = await apiClient.post<Request>(`${this.baseUrl}/${id}/duplicate`);
    return response.data;
  }

  async archiveRequest(id: string): Promise<void> {
    await apiClient.post(`${this.baseUrl}/${id}/archive`);
  }

  // ================== STATISTICHE ==================
  async getRequestStatistics(): Promise<any> {
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics`);
    return response.data;
  }

  async getRequestStatisticsByDateRange(startDate: string, endDate: string): Promise<any> {
    const params = { startDate, endDate };
    const queryString = buildQueryParams(params);
    const response = await apiClient.get<any>(`${this.baseUrl}/statistics/by-date-range?${queryString}`);
    return response.data;
  }

  async getRequestCountByType(): Promise<Record<RequestType, number>> {
    const response = await apiClient.get<Record<RequestType, number>>(`${this.baseUrl}/count-by-type`);
    return response.data;
  }

  async getRequestCountByClient(): Promise<Record<string, number>> {
    const response = await apiClient.get<Record<string, number>>(`${this.baseUrl}/count-by-client`);
    return response.data;
  }

  async getAverageCompletionTime(): Promise<number> {
    const response = await apiClient.get<number>(`${this.baseUrl}/average-completion-time`);
    return response.data;
  }

  // ================== PROGRESSIVE NUMBER ==================

  async getProgressiveNumberSuggestion(
    requestType: RequestType,
    year?: number
  ): Promise<ProgressiveNumberSuggestion> {
    const params = new URLSearchParams();
    params.append('requestType', String(requestType));
    if (year) {
      params.append('year', String(year));
    }
    const response = await apiClient.get<ProgressiveNumberSuggestion>(
      `${this.baseUrl}/progressive-number/suggestion?${params.toString()}`
    );
    return response.data;
  }

  async validateProgressiveNumber(
    number: number,
    year: number,
    requestType: RequestType,
    excludeRequestId?: number
  ): Promise<ProgressiveNumberValidation> {
    const params = new URLSearchParams();
    params.append('number', String(number));
    params.append('year', String(year));
    params.append('requestType', String(requestType));
    if (excludeRequestId) {
      params.append('excludeRequestId', String(excludeRequestId));
    }
    const response = await apiClient.get<ProgressiveNumberValidation>(
      `${this.baseUrl}/progressive-number/validate?${params.toString()}`
    );
    return response.data;
  }
}

export const requestService = new RequestService();

// ================== HELPERS ==================

function normalizeAnalyticsRow(raw: any): RequestAnalyticsRow {
  const r = raw ?? {};
  return {
    id: Number(r.id) || 0,
    requestNumber: r.requestNumber ?? '',
    progressiveNumber: Number(r.progressiveNumber) || 0,
    progressiveYear: Number(r.progressiveYear) || 0,
    progressiveNumberFormatted:
      r.progressiveNumberFormatted ?? `${r.progressiveNumber ?? ''}/${r.progressiveYear ?? ''}`,
    type: toRequestTypeNum(r.type) as unknown as RequestType,
    status: toRequestStatusNum(r.status) as unknown as RequestStatus,
    title: r.title ?? '',
    clientId: r.clientId ?? undefined,
    clientName: r.clientName ?? '',
    createdAt: r.createdAt ?? '',
    completedAt: r.completedAt ?? undefined,
    preCampionaturaCompletedAt: r.preCampionaturaCompletedAt ?? undefined,
    durationDays: r.durationDays != null ? Number(r.durationDays) : undefined,
    preCampionaturaDurationDays: r.preCampionaturaDurationDays != null ? Number(r.preCampionaturaDurationDays) : undefined,
    campionaturaDurationDays: r.campionaturaDurationDays != null ? Number(r.campionaturaDurationDays) : undefined,
  };
}

function normalizeAggregates(raw: any): RequestAggregates {
  const data = raw ?? {};
  return {
    byType: data.byType ?? {},
    byTypeAndStatus: (data.byTypeAndStatus ?? []).map((b: any) => ({
      type: toRequestTypeNum(b?.type),
      status: toRequestStatusNum(b?.status),
      count: Number(b?.count) || 0,
    })),
    monthlyTrend: (data.monthlyTrend ?? []).map((b: any) => ({
      year: Number(b?.year) || 0,
      month: Number(b?.month) || 0,
      type: toRequestTypeNum(b?.type),
      count: Number(b?.count) || 0,
    })),
    topClients: (data.topClients ?? []).map((c: any) => ({
      clientId: c?.clientId ?? undefined,
      clientName: c?.clientName ?? '',
      count: Number(c?.count) || 0,
    })),
    completion: {
      averageDaysByType: data.completion?.averageDaysByType ?? {},
      medianDaysByType: data.completion?.medianDaysByType ?? {},
      p90DaysByType: data.completion?.p90DaysByType ?? {},
      overallAverageDays: Number(data.completion?.overallAverageDays) || 0,
      preCampionatura: data.completion?.preCampionatura
        ? {
            averageDays: Number(data.completion.preCampionatura.averageDays) || 0,
            medianDays: Number(data.completion.preCampionatura.medianDays) || 0,
            p90Days: Number(data.completion.preCampionatura.p90Days) || 0,
            count: Number(data.completion.preCampionatura.count) || 0,
          }
        : undefined,
    },
    availableYears: (data.availableYears ?? []).map((y: any) => Number(y)).filter((y: number) => y > 0),
    totalCount: Number(data.totalCount) || 0,
  } as RequestAggregates;
}
